import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { message } from 'antd';
import { capitalize } from 'lodash';
import moment from 'moment';
import IntercityTripForm from 'components/forms/IntercityTripForm';
import { getClientList, getLocations } from 'store/clientSlice';
import { addUser, getPincodeList } from 'store/pageSlice';
import { getSignedUrl } from 'store/commonSlice';
import { search_type, getuniqueArray } from 'utils';
import { useAuth } from 'hooks';
import { getProjectListV2 } from 'store/projectSliceV2';
import { getRouteListV2 } from 'store/routeSliceV2';
import { searchVendorV2 } from 'store/vendorSliceV2';
import { searchVehicleTypesV2, searchVehiclesV2 } from 'store/vehicleSliceV2';
import { searchDriversV2 } from 'store/driverSliceV2';
import { getTripListActionV2 } from 'store/tripSliceV2';
import { reuploadPoR } from 'store/approvalSlice';

const toUtcDateTime = (date) =>
	date ? moment(date).subtract(330, 'minutes').format('YYYY-MM-DDTHH:mm:ss') : null;
const toUtcTime = (date) =>
	date ? moment(date).subtract(330, 'minutes').format('HH:mm:ss') : null;

export default function CreateIntercityTripV2() {
	const { permissions } = useAuth();
	const [formData, setFormData] = useState({});
	const [trip_reason, setTripReason] = useState([]);
	const { cities = [] } = useSelector((state) => state.clientState);
	const dispatch = useDispatch();
	const { id } = useParams();
	const isEdit = !!id;
	const [projectData, setProjects] = useState([]);
	const [routeData, setRouteData] = useState([]);
	const [vendorsData, setVendordata] = useState([]);
	const [VehicleTypes, setVehicleTypes] = useState([]);
	const [vehicleNumbers, setVehicleNumbers] = useState([]);
	const [driverData, setDriverData] = useState([]);
	const [vehicleTypeIdMap, setVehicleTypeIdMap] = useState({});
	const [ProjectAdhocFlag, SetProjectAdhocFlag] = useState(false);
	const [crossVendorAllowed, setCrossVendorAllowed] = useState(false);
	const [sourceCity, setSourceCity] = useState('');
	const [destCity, setDestCity] = useState('');
	const [uploading, setUploading] = useState(false);
	const [uploadError, setUploadError] = useState('');
	const [showLRPrompt, setShowLRPrompt] = useState(false);
	const [pendingValues, setPendingValues] = useState(null);
	const [modalLoading, setModalLoading] = useState(false);
	const [loading, setLoading] = useState(false);
	const formRef = useRef();
	// The Proof of Request document key as loaded (before edits) — used to detect a
	// PoR replacement on save so we can fire the separate reupload_por call.
	const originalPorKeyRef = useRef(null);
	const navigate = useNavigate();
	const location = useLocation();
	const [isvendorBilling, setIsvendorBilling] = React.useState(false);
	// This component is only used in new UI, so always use V2 APIs
	const isV2UI = true;
	const vendorUserType = [
		{ value: 'regular', label: 'Regular' },
		{ value: 'temporary', label: 'Temporary' },
	];

	const mergeSelectedRouteOption = React.useCallback((routes, selectedRoute) => {
		if (!selectedRoute?.value || !selectedRoute?.label) {
			return routes;
		}

		const hasSelectedRoute = routes.some(
			(route) => String(route?.value) === String(selectedRoute.value)
		);

		if (hasSelectedRoute) {
			return routes;
		}

		return [selectedRoute, ...routes];
	}, []);

	// Helper function to load projects by city
	// This component is only used in new UI, so always use V2 API
	const loadProjectsByCity = React.useCallback(
		async (city) => {
			if (!city) return;

			await dispatch(
				getProjectListV2({
					filter_by: {
						city,
						is_active: true,
					},
					pagination: { page: 1, per_page: 1000 },
				})
			).then((projectRes) => {
				const prodata = (projectRes?.rows || []).map((item) => ({
					label: capitalize(item?.project_name || '-'),
					value: item?.id,
				}));
				setProjects(prodata);
			});
		},
		[dispatch]
	);

	const fetchRoutesByProject = React.useCallback(
		(projectId) => {
			if (!projectId) {
				setRouteData([]);
				return Promise.resolve();
			}

			return dispatch(
				getRouteListV2({
					filter_by: {
						project_id: Number(projectId),
						is_active: true,
					},
					pagination: { page: 1, per_page: 100 },
					sort_by: { field: 'created_at', order: 'desc' },
				})
			)
				.then((routeRes) => {
					const routes = routeRes?.rows || [];
					const mappedRoutes = routes.map((route) => {
						const routeValue = route.id ?? route.route_id;
						return {
							value: String(routeValue),
							label:
								route.route_name ||
								route.name ||
								route.route_code ||
								`Route ${routeValue}`,
						};
					});
					setRouteData((prevRouteData) => {
						const selectedRoute = prevRouteData.find(
							(route) => String(route?.value) === String(formData?.route_id)
						);
						return mergeSelectedRouteOption(mappedRoutes, selectedRoute);
					});
				})
				.catch(() => {
					setRouteData([]);
				});
		},
		[dispatch, formData?.route_id, mergeSelectedRouteOption]
	);

	// Helper function to load vendor data for regular trips
	const loadVendorDataForRegularTrips = React.useCallback(
		(projectId, city) => {
			if (!projectId) return;

			dispatch(
				getClientList({
					search_type: 'vendor_contract',
					filter_by: {
						project_id: projectId,
						city,
						is_active: true,
					},
				})
			).then((vendorRes) => {
				const arr = Array.isArray(vendorRes?.data)
					? vendorRes?.data
					: Object.values(vendorRes?.data || {});
				const data_vendor = arr.map((item) => ({
					value: item?.vendor_id,
					label: item?.vendor_name,
				}));
				setVendordata(data_vendor);
			});
		},
		[dispatch]
	);

	// Helper function to load vehicle numbers based on vendor and vehicle type
	const loadVehicleNumbers = React.useCallback(
		async (vendorId, vehicleTypeId, currentVehicleNumber = null) => {
			if (!vendorId || !vehicleTypeId) {
				setVehicleNumbers([]);
				return;
			}

			try {
				const vehicleRes = await dispatch(
					searchVehiclesV2({
						filter_by: {
							vendor_id: vendorId,
							vehicle_type_id: Number(vehicleTypeId),
							is_active: true,
						},
						pagination: { page: 1, per_page: 1000 },
						sort_by: { field: 'created_at', order: 'desc' },
					})
				);

				const vehicles = vehicleRes?.rows || [];
				const vehicleOptions = vehicles.map((vehicle) => ({
					value: vehicle.vehicle_number,
					label: vehicle.vehicle_number || '-',
					item: vehicle,
				}));

				// In edit mode, ensure current vehicle_number is in the list
				if (
					currentVehicleNumber &&
					!vehicleOptions.find((v) => v.value === currentVehicleNumber)
				) {
					vehicleOptions.unshift({
						value: currentVehicleNumber,
						label: currentVehicleNumber,
					});
				}

				setVehicleNumbers(vehicleOptions);
			} catch (error) {
				setVehicleNumbers([]);
			}
		},
		[dispatch]
	);

	// Helper function to resolve vehicle_type_id from vehicle_type string
	async function resolveVehicleTypeId(vehicleTypeName) {
		if (!vehicleTypeName) return null;

		const mappedTypeId = vehicleTypeIdMap[vehicleTypeName];
		if (mappedTypeId) {
			return Number(mappedTypeId);
		}

		try {
			const res = await dispatch(
				searchVehicleTypesV2({
					filter_by: { is_active: true },
					pagination: { page: 1, per_page: 200 },
				})
			);
			const rows = res?.rows || [];
			const matchedType = rows.find((item) => {
				const typeName = item?.vehicle_type_name || item?.name;
				return (
					String(typeName || '').toLowerCase() === String(vehicleTypeName).toLowerCase()
				);
			});

			if (matchedType) {
				const typeId = matchedType.id;
				setVehicleTypeIdMap((prev) => ({ ...prev, [vehicleTypeName]: typeId }));
				return Number(typeId);
			}
		} catch (err) {
			// Error resolving vehicle type ID
		}
		return null;
	}

	// Helper function to fetch drivers filtered by vendor_id and vehicle_type_id
	async function getDriverDataV2(
		vendorId,
		vehicleTypeId,
		currentDriverId = null,
		currentDriverName = null
	) {
		if (
			!vendorId ||
			!vehicleTypeId ||
			!(permissions?.search?.includes('driver') || permissions?.search?.includes('all'))
		) {
			setDriverData([]);
			return [];
		}

		try {
			const res = await dispatch(
				searchDriversV2({
					filter_by: {
						vendor_id: vendorId,
						vehicle_type_ids: [Number(vehicleTypeId)],
						is_active: true,
					},
					offset: 0,
					limit: 1000,
				})
			);
			const data = (res?.rows || []).map((item) => ({
				value: item?.id,
				label: item?.driver_name || item?.name || '-',
				item,
			}));

			// In edit mode, ensure current driver is in the list
			if (currentDriverId) {
				const driverIdNum = Number(currentDriverId);
				if (!data.find((d) => Number(d.value) === driverIdNum)) {
					data.unshift({
						value: driverIdNum, // Ensure value is a number to match option values
						label: currentDriverName || `Driver ID: ${currentDriverId}`,
						item: { id: driverIdNum, driver_name: currentDriverName },
					});
				}
			} else if (
				currentDriverName &&
				!data.find(
					(d) =>
						d.item?.driver_name === currentDriverName ||
						d.item?.name === currentDriverName
				)
			) {
				// If we have driver_name but no driver_id, add it to the list
				data.unshift({
					value: null, // Or a temporary ID if needed
					label: currentDriverName,
					item: { driver_name: currentDriverName },
				});
			}

			setDriverData(data);
			return data;
		} catch (err) {
			setDriverData([]);
			return [];
		}
	}

	// Function to load dependent data based on form values
	const loadDependentData = React.useCallback(
		async (formValues) => {
			if (!formValues) return;

			// Load project data for the city - wait for it to complete
			if (formValues.city) {
				await loadProjectsByCity(formValues.city);
			}

			// Load vendor data for regular trips
			if (formValues.trip_type === 'regular' && formValues.project_id) {
				loadVendorDataForRegularTrips(formValues.project_id, formValues.city);
			}

			if (formValues.project_id) {
				await fetchRoutesByProject(formValues.project_id);
			}

			// Load vendor data for adhoc trips
			if (formValues.trip_type === 'adhoc') {
				getVendorDataList(formValues.vendor_type);
			}

			// Load vehicle types
			getVehicleTypes();

			// Load vehicle numbers if both vendor_id and vehicle_type are present
			if (formValues.vendor_id && formValues.vehicle_type) {
				const vehicleTypeId = await resolveVehicleTypeId(formValues.vehicle_type);
				if (vehicleTypeId) {
					await loadVehicleNumbers(
						formValues.vendor_id,
						vehicleTypeId,
						formValues.vehicle_number,
						formValues.route_id ?? null
					);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			fetchRoutesByProject,
			loadProjectsByCity,
			loadVendorDataForRegularTrips,
			loadVehicleNumbers,
		]
	);

	// Check for restored form data from create-lr or adhoc-request and load dependent data
	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		if (
			location.state?.formData &&
			(location.state?.source === 'create-lr' || location.state?.source === 'adhoc-request')
		) {
			const restoredFormData = location.state.formData;
			if (restoredFormData.route_id && restoredFormData.route_name) {
				setRouteData([
					{
						value: String(restoredFormData.route_id),
						label: restoredFormData.route_name,
					},
				]);
			}

			// If we have a project_id, fetch that specific project to get its name
			// This component is only used in new UI, so always use V2 API
			if (restoredFormData.project_id) {
				if (restoredFormData.project_name) {
					setProjects([
						{
							value: restoredFormData.project_id,
							label: capitalize(restoredFormData.project_name),
						},
					]);
				}
				dispatch(
					getProjectListV2({
						filter_by: {
							id: restoredFormData.project_id,
						},
						pagination: { page: 1, per_page: 1 },
					})
				).then((projectRes) => {
					const projectItem = projectRes?.rows?.[0];
					if (projectItem) {
						setProjects([
							{
								value: projectItem.id,
								label: capitalize(projectItem?.project_name || '-'),
							},
						]);
						setCrossVendorAllowed(projectItem?.allow_cross_vendor_vehicle || false);
					}
				});
			}

			// Load dependent data first, then set form data
			loadDependentData(restoredFormData).then(async () => {
				if (restoredFormData.route_id && restoredFormData.route_name) {
					setRouteData((prevRouteData) => {
						const preservedRoute = {
							value: String(restoredFormData.route_id),
							label: restoredFormData.route_name,
						};
						return mergeSelectedRouteOption(prevRouteData, preservedRoute);
					});
				}
				// Fetch drivers if vehicle_number, vendor_id, and vehicle_type are present
				if (
					restoredFormData.vehicle_number &&
					restoredFormData.vendor_id &&
					restoredFormData.vehicle_type
				) {
					const vehicleTypeId = await resolveVehicleTypeId(restoredFormData.vehicle_type);
					if (vehicleTypeId) {
						const drivers = await getDriverDataV2(
							restoredFormData.vehicle_vendor_id || restoredFormData.vendor_id,
							vehicleTypeId,
							restoredFormData.driver_id,
							restoredFormData.driver_name
						);

						// Try to find driver_id from driver_name if driver_name exists but driver_id doesn't
						if (restoredFormData.driver_name && !restoredFormData.driver_id) {
							const matchedDriver = drivers.find(
								(driver) =>
									driver.item?.driver_name === restoredFormData.driver_name ||
									driver.item?.name === restoredFormData.driver_name
							);
							if (matchedDriver) {
								restoredFormData.driver_id = matchedDriver.value;
							}
						}
					}
				}

				// Update form data with restored values after dependent data is loaded
				setFormData((prevData) => ({
					...prevData,
					...restoredFormData,
				}));
			});

			// Fetch city and state information for pincodes when coming from adhoc-request or create-lr
			if (
				location.state?.source === 'adhoc-request' ||
				location.state?.source === 'create-lr'
			) {
				// Fetch city/state for source pincode
				if (restoredFormData.start_location_pincode) {
					dispatch(getPincodeList(restoredFormData.start_location_pincode))
						.then((resp) => {
							const data = resp.rows[0];
							const cityState = data
								? `${data.city || ''}${data.city && data.state ? ', ' : ''}${
										data.state || ''
								  }`
								: '';
							setSourceCity(cityState);
						})
						.catch(() => {
							// Error fetching source pincode details
						});
				}

				// Fetch city/state for destination pincode
				if (restoredFormData.end_location_pincode) {
					dispatch(getPincodeList(restoredFormData.end_location_pincode))
						.then((response) => {
							const data = response.rows[0];
							const cityState = data
								? `${data.city || ''}${data.city && data.state ? ', ' : ''}${
										data.state || ''
								  }`
								: '';
							setDestCity(cityState);
						})
						.catch(() => {
							// Error fetching destination pincode details
						});
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.state, loadDependentData, dispatch, isV2UI, mergeSelectedRouteOption]);

	useEffect(() => {
		dispatch(
			getClientList({
				search_type: 'trip_end_reason',
				filter_by: {},
			})
		).then((res) => {
			const reason_data = res?.data?.map((item) => ({
				value: item?.id,
				label: item?.reason,
			}));
			setTripReason(reason_data);
		});
		if (permissions?.search?.includes('location') || permissions?.search?.includes('all')) {
			dispatch(
				getLocations({
					search_type: search_type?.location,
					filter_by: {},
				})
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, id]);

	// Watch for vendor_id and vehicle_type changes to fetch vehicle numbers
	useEffect(() => {
		async function fetchVehicleNumbers() {
			if (formData.vendor_id && formData.vehicle_type) {
				const vehicleTypeId = await resolveVehicleTypeId(formData.vehicle_type);
				if (vehicleTypeId) {
					await loadVehicleNumbers(
						formData.vendor_id,
						vehicleTypeId,
						formData.vehicle_number,
						formData.route_id ?? null
					);
				} else {
					setVehicleNumbers([]);
				}
			} else {
				setVehicleNumbers([]);
			}
		}
		fetchVehicleNumbers();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		formData.vendor_id,
		formData.vehicle_type,
		formData.vehicle_number,
		formData.route_id,
		loadVehicleNumbers,
	]);

	// Watch for vehicle_number changes to fetch drivers
	useEffect(() => {
		async function fetchDrivers() {
			if (formData.vehicle_number && formData.vendor_id && formData.vehicle_type) {
				const vehicleTypeId = await resolveVehicleTypeId(formData.vehicle_type);
				if (vehicleTypeId) {
					await getDriverDataV2(
						formData.vehicle_vendor_id || formData.vendor_id,
						vehicleTypeId,
						formData.driver_id,
						formData.driver_name
					);
				} else {
					setDriverData([]);
				}
			} else {
				setDriverData([]);
			}
		}
		fetchDrivers();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		formData.vehicle_number,
		formData.vendor_id,
		formData.vehicle_type,
		formData.driver_id,
		formData.driver_name,
	]);

	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		if (id) {
			setLoading(true);
			// Use V2 API for new UI
			dispatch(
				getTripListActionV2({
					search_type: 'trip',
					id,
					limit: 1,
					offset: 0,
					fetch_filters: false,
				})
			)
				.then((res) => {
					const trip = res?.rows?.[0] || {};
					const isTripEndedInV2 =
						trip.trip_status_id === 3 ||
						(typeof trip.trip_status === 'string' &&
							trip.trip_status.toLowerCase() === 'trip_closed');
					// Map API response to form fields expected by IntercityTripForm
					const info = trip.info || {};
					// Build picker datetimes from date fields plus time parts from the datetime fields.
					setIsvendorBilling(trip?.is_vendor_billed || false);
					const buildFormDateTime = (fieldName, dateValue, dateTimeValue) => {
						if (!dateValue && !dateTimeValue) return undefined;

						const normalizedDate =
							typeof dateValue === 'string' ? dateValue.trim() : dateValue;
						const normalizedDateTime =
							typeof dateTimeValue === 'string'
								? dateTimeValue.trim()
								: dateTimeValue;

						let extractedDate = '';
						if (typeof normalizedDate === 'string') {
							const dateMatch = normalizedDate.match(
								/^(\d{2}\/\d{2}\/\d{4}|\d{4}-\d{2}-\d{2})/
							);
							extractedDate = dateMatch?.[1] || '';
						}

						let extractedTime = '';
						if (typeof normalizedDateTime === 'string') {
							const allTimeMatches = normalizedDateTime.match(/\d{2}:\d{2}:\d{2}/g);
							extractedTime = allTimeMatches?.[allTimeMatches.length - 1] || '';
						}

						let combinedDateTime = normalizedDateTime || normalizedDate;
						if (extractedDate && extractedTime) {
							combinedDateTime = `${extractedDate} ${extractedTime}`;
						} else if (
							extractedDate &&
							!extractedTime &&
							typeof normalizedDate === 'string'
						) {
							const dateSideTimeMatch = normalizedDate.match(/(\d{2}:\d{2}:\d{2})/);
							if (dateSideTimeMatch?.[1]) {
								combinedDateTime = `${extractedDate} ${dateSideTimeMatch[1]}`;
							}
						}
						const parsedMoment = moment(
							combinedDateTime,
							['MM/DD/YYYY HH:mm:ss', 'YYYY-MM-DD HH:mm:ss', moment.ISO_8601],
							true
						);
						if (parsedMoment.isValid()) {
							return parsedMoment;
						}
						const parsedDate = new Date(combinedDateTime);
						return Number.isNaN(parsedDate.getTime()) ? undefined : moment(parsedDate);
					};

					const tripFormData = {
						id: trip.id, // <-- ensure id is present
						trip_type: trip.trip_type || 'adhoc',
						city: trip.city || '',
						project_id: trip.project_id || info.project_id || '',
						route_id: (() => {
							if (trip.route_id !== undefined && trip.route_id !== null) {
								return String(trip.route_id);
							}
							if (info.route_id !== undefined && info.route_id !== null) {
								return String(info.route_id);
							}
							return '';
						})(),
						route_name: trip.route_name || '',
						vendor_id: trip.vendor_id || info.vendor_id || '',
						vendor_type: trip.vendor_type || info.vendor_type || '',
						project_location_code:
							trip.project_location_code || info.project_location_code || '',
						vendor_name: trip.vendor_name || info.vendor_name || '',
						vehicle_number: trip.vehicle_number || '',
						reporting_date: buildFormDateTime(
							'reporting_date',
							trip.reporting_date,
							trip.reporting_date
						),
						remarks: trip.remarks || info.remarks || '',
						start_date: buildFormDateTime(
							'start_date',
							trip.start_date,
							trip.start_time
						),
						start_location_pincode: trip.source_pincode || '',
						end_location_pincode: trip.destination_pincode || '',
						vehicle_type: trip.vehicle_type || '',
						driver_id: trip.driver_id || info.driver_id || undefined, // Ensure driver_id is included
						driver_name: trip.driver_name || info.driver_name || '',
						driver_number:
							trip.driver_number || info.driver_number || trip.driver_mobile || '',
						requirement_proof:
							(trip.por_approval_status || info.por_approval_status) === 'rejected'
								? []
								: info.requirement_proof || trip.requirement_proof || [],
						por_approval_status:
							trip.por_approval_status || info.por_approval_status || null,
						is_approved: trip.is_approved === true,
						client_rate: info.client_rate || trip.client_trip_cost || '',
						vendor_rate: info.vendor_rate || trip.vendor_trip_cost || '',
						vendor_rate_basis: trip.vendor_rate_basis || info.vendor_rate_basis || '',
						billing_type: trip.billing_type || info.billing_type || '',
						billing_subtype: trip.billing_subtype || info.billing_subtype || '',
						current_vendor_rate: trip.current_vendor_rate,
						current_charges_per_km: trip.current_charges_per_km,
						order_rates: trip.order_rates || info.order_rates || [],
						start_km: trip.start_km ?? '',
						trip_status_id: trip.trip_status_id,
						isEnded: isTripEndedInV2,
						// Cross-vendor vehicle: client eligibility + whether this trip already uses one
						allow_cross_vendor_vehicle: trip.allow_cross_vendor_vehicle || false,
						is_cross_vendor_vehicle: trip.is_cross_vendor_vehicle || false,
						vehicle_vendor_name: trip.vehicle_vendor_name || '',
						vehicle_vendor_id: trip.vehicle_vendor_id || null,
					};

					if (tripFormData.route_id && tripFormData.route_name) {
						setRouteData([
							{
								value: String(tripFormData.route_id),
								label: tripFormData.route_name,
							},
						]);
					}

					// If we have a project_id, fetch that specific project to get its name
					// This component is only used in new UI, so always use V2 API
					if (tripFormData.project_id) {
						dispatch(
							getProjectListV2({
								filter_by: {
									id: tripFormData.project_id,
								},
								pagination: { page: 1, per_page: 1 },
							})
						).then((projectRes) => {
							const projectItem = projectRes?.rows?.[0];
							if (projectItem) {
								setProjects([
									{
										value: projectItem.id,
										label: capitalize(projectItem?.project_name || '-'),
									},
								]);
							}
						});
					}

					// Load dependent data first, then set form data
					loadDependentData(tripFormData).then(async () => {
						if (tripFormData.route_id && tripFormData.route_name) {
							setRouteData((prevRouteData) => {
								const preservedRoute = {
									value: String(tripFormData.route_id),
									label: tripFormData.route_name,
								};
								return mergeSelectedRouteOption(prevRouteData, preservedRoute);
							});
						}

						// Load vehicle numbers with current vehicle_number included
						if (tripFormData.vendor_id && tripFormData.vehicle_type) {
							const vehicleTypeId = await resolveVehicleTypeId(
								tripFormData.vehicle_type
							);
							if (vehicleTypeId) {
								await loadVehicleNumbers(
									tripFormData.vendor_id,
									vehicleTypeId,
									tripFormData.vehicle_number
								);
							}
						}

						// Fetch drivers if vehicle_number, vendor_id, and vehicle_type are present
						if (
							tripFormData.vehicle_number &&
							tripFormData.vendor_id &&
							tripFormData.vehicle_type
						) {
							const vehicleTypeId = await resolveVehicleTypeId(
								tripFormData.vehicle_type
							);
							if (vehicleTypeId) {
								const drivers = await getDriverDataV2(
									tripFormData.vehicle_vendor_id || tripFormData.vendor_id,
									vehicleTypeId,
									tripFormData.driver_id,
									tripFormData.driver_name
								);

								// Try to find driver_id from driver_name if driver_name exists but driver_id doesn't
								if (tripFormData.driver_name && !tripFormData.driver_id) {
									const matchedDriver = drivers.find(
										(driver) =>
											driver.item?.driver_name === tripFormData.driver_name ||
											driver.item?.name === tripFormData.driver_name
									);
									if (matchedDriver) {
										tripFormData.driver_id = matchedDriver.value;
									}
								}
							}
						}
						originalPorKeyRef.current =
							(info.requirement_proof || trip.requirement_proof || [])[0] || null;

						setFormData(tripFormData);
					});

					// Fetch city and state information for existing pincodes in edit mode
					if (isEdit && tripFormData.start_location_pincode) {
						dispatch(getPincodeList(tripFormData.start_location_pincode))
							.then((resp) => {
								const data = resp.rows?.[0];
								const cityState = data
									? `${data.city || ''}${data.city && data.state ? ', ' : ''}${
											data.state || ''
									  }`
									: '';
								setSourceCity(cityState);
							})
							.catch(() => {
								// Error fetching source pincode details
							});
					}

					if (isEdit && tripFormData.end_location_pincode) {
						dispatch(getPincodeList(tripFormData.end_location_pincode))
							.then((response) => {
								const data = response.rows?.[0];
								const cityState = data
									? `${data.city || ''}${data.city && data.state ? ', ' : ''}${
											data.state || ''
									  }`
									: '';
								setDestCity(cityState);
							})
							.catch(() => {
								// Error fetching destination pincode details
							});
					}

					setLoading(false);
				})
				.catch(() => setLoading(false));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, id, loadDependentData]);

	function handleCitiesChange(cityValue, setFieldValue) {
		setFieldValue('project_id', undefined);
		setFieldValue('route_id', undefined);
		setRouteData([]);
		if (!cityValue) {
			setProjects([]);
			return;
		}
		// This component is only used in new UI, so always use V2 API
		dispatch(
			getProjectListV2({
				filter_by: {
					city: cityValue,
					is_active: true,
				},
				pagination: { page: 1, per_page: 1000 },
			})
		).then((res) => {
			const prodata = (res?.rows || []).map((item) => ({
				label: capitalize(item?.project_name || '-'),
				value: item?.id,
			}));
			setProjects(prodata);
		});
	}

	// eslint-disable-next-line sonarjs/cognitive-complexity
	function handleProjectChange(e, setFieldValue, values) {
		if (setFieldValue) {
			setFieldValue('project_id', e);
			setFieldValue('route_id', undefined);
			setFieldValue('vehicle_type', undefined);
			setFieldValue('driver_id', undefined);
			setFieldValue('vendor_id', undefined);
			setFieldValue('vehicle_number', undefined);
			setFieldValue('driver_number', '');
			setFieldValue('driver_name', '');
		}
		setVehicleNumbers([]);
		setDriverData([]);
		fetchRoutesByProject(e);
		// For regular trips: fetch vendor contracts and vehicle types
		if (
			values.trip_type === 'regular' &&
			(permissions?.search?.includes('vendor_contract') ||
				permissions?.search?.includes('all'))
		) {
			dispatch(
				getClientList({
					search_type: 'vendor_contract',
					filter_by: {
						project_id: e,
						city: values?.city,
						is_active: true,
					},
				})
			).then((res) => {
				const arr = Array.isArray(res?.data) ? res?.data : Object.values(res?.data || {});
				const data_vendor = arr.map((item) => ({
					value: item?.vendor_id,
					label: item?.vendor_name,
				}));
				const data_vechicleType = arr.map((item) => ({
					value: item?.vehicle_type,
					label: item?.vehicle_type,
				}));
				setVehicleTypes(getuniqueArray(data_vechicleType));
				setVendordata(getuniqueArray(data_vendor));
			});
		}
		// For adhoc trips: fetch project details and vehicle types, set adhoc flag
		if (
			values.trip_type === 'adhoc' &&
			(permissions?.search?.includes('project') || permissions?.search?.includes('all'))
		) {
			getVehicleTypes();
			getVendorDataList();
			// This component is only used in new UI, so always use V2 API
			dispatch(
				getProjectListV2({
					filter_by: {
						id: e,
						is_active: true,
					},
					pagination: { page: 1, per_page: 1 },
				})
			).then((pro_res) => {
				const projectItem = pro_res?.rows?.[0];
				if (projectItem) {
					SetProjectAdhocFlag(projectItem?.adhoc_approval_required || false);
					setCrossVendorAllowed(projectItem?.allow_cross_vendor_vehicle || false);
				}
			});
		}
	}
	async function handleCrossVendorSelect(payload, setFieldValue, values) {
		if (!payload?.vehicle_number) return;
		// Cross-vendor vehicle can be of any type — switch the trip's vehicle type to the chosen
		// vehicle's type and remember its id so driver loading + submit resolve correctly.
		if (payload.vehicle_type_name) {
			setFieldValue('vehicle_type', payload.vehicle_type_name);
			if (payload.vehicle_type_id) {
				setVehicleTypeIdMap((prev) => ({
					...prev,
					[payload.vehicle_type_name]: payload.vehicle_type_id,
				}));
			}
		}
		// Inject the chosen vehicle into the dropdown options so it displays.
		setVehicleNumbers((prev) =>
			prev.find((o) => o.value === payload.vehicle_number)
				? prev
				: [
						{
							value: payload.vehicle_number,
							label: payload.vehicle_number,
							item: {
								id: payload.vehicle_id,
								vehicle_number: payload.vehicle_number,
								vendor_id: payload.vendor_id,
								vehicle_type_id: payload.vehicle_type_id,
							},
						},
						...prev,
				  ]
		);
		setFieldValue('vehicle_number', payload.vehicle_number);
		setFieldValue('cross_vendor_remark', payload.remark || ''); // optional; stored in trip_info
		setFieldValue('driver_id', undefined);
		setFieldValue('driver_name', '');
		setFieldValue('driver_number', '');
		// Load drivers by the chosen vehicle's vendor + its vehicle type.
		const typeId =
			payload.vehicle_type_id ||
			(await resolveVehicleTypeId(payload.vehicle_type_name || values?.vehicle_type));
		if (payload.vendor_id && typeId) {
			await getDriverDataV2(payload.vendor_id, typeId);
		}
	}

	function getVehicleTypes() {
		// This component is only used in new UI, so always use V2 API
		dispatch(
			searchVehicleTypesV2({
				filter_by: {},
				pagination: { page: 1, per_page: 1000 },
			})
		).then((resVehical) => {
			const vehicleTypes = (resVehical?.rows || []).map((item) => {
				const typeName = item?.vehicle_type_name || item?.name;
				const typeId = item?.id;

				// Store mapping for vehicle_type_id resolution
				if (typeName && typeId) {
					setVehicleTypeIdMap((prev) => ({ ...prev, [typeName]: typeId }));
				}

				return {
					value: typeName,
					label: typeName,
					id: typeId, // Store ID for potential V2 API submission
				};
			});
			setVehicleTypes(getuniqueArray(vehicleTypes));
		});
	}

	function getVendorDataList(type) {
		// This component is only used in new UI, so always use V2 API
		dispatch(
			searchVendorV2({
				filter_by: {
					is_active: true,
					...(type ? { vendor_type: type } : {}),
				},
				pagination: { page: 1, per_page: 1000 },
			})
		).then((res) => {
			const data_vendor = (res?.rows || []).map((item) => ({
				value: item?.id,
				label: item?.vendor_name || item?.name || item?.company_name,
			}));
			setVendordata(data_vendor);
		});
	}

	function handleVendorTypeChange(type, setFieldValue) {
		setFieldValue('vendor_id', undefined);
		setFieldValue('vehicle_number', undefined);
		setFieldValue('driver_id', undefined);
		setFieldValue('driver_number', '');
		setFieldValue('driver_name', '');
		setDriverData([]);
		getVendorDataList(type);
	}

	function handleRouteChange(routeId, setFieldValue) {
		setFieldValue('route_id', routeId);
		setFieldValue('vehicle_number', undefined);
		setFieldValue('driver_id', undefined);
		setFieldValue('driver_number', '');
		setFieldValue('driver_name', '');
		setVehicleNumbers([]);
		setDriverData([]);
	}

	async function handleVendorChange(vendorId, setFieldValue, vehicleType, values) {
		setFieldValue('vendor_id', vendorId);
		setFieldValue('vehicle_number', undefined);
		setFieldValue('driver_id', undefined);
		setFieldValue('driver_number', '');
		setFieldValue('driver_name', '');
		setDriverData([]);
		if (vendorId && vehicleType) {
			const vehicleTypeId = await resolveVehicleTypeId(vehicleType);
			if (vehicleTypeId) {
				await loadVehicleNumbers(vendorId, vehicleTypeId, null, values?.route_id ?? null);
			} else {
				setVehicleNumbers([]);
			}
		} else {
			setVehicleNumbers([]);
		}
	}

	async function handleVehicleTypeChange(vehicleType, setFieldValue, vendorId, values) {
		setFieldValue('vehicle_type', vehicleType);
		setFieldValue('vehicle_number', undefined);
		setFieldValue('driver_id', undefined);
		setFieldValue('driver_number', '');
		setDriverData([]);
		if (vendorId && vehicleType) {
			const vehicleTypeId = await resolveVehicleTypeId(vehicleType);
			if (vehicleTypeId) {
				await loadVehicleNumbers(vendorId, vehicleTypeId, null, values?.route_id ?? null);
			} else {
				setVehicleNumbers([]);
			}
		} else {
			setVehicleNumbers([]);
		}
	}

	async function handleVehicleNumberChange(vehicleNumber, setFieldValue, values) {
		setFieldValue('vehicle_number', vehicleNumber);
		setFieldValue('driver_id', undefined);
		setFieldValue('driver_number', '');
		setFieldValue('driver_name', '');
		setDriverData([]);

		if (!vehicleNumber) return;

		const vehicleTypeId = await resolveVehicleTypeId(values?.vehicle_type);
		const selectedVehicle = vehicleNumbers.find((v) => v.value === vehicleNumber)?.item;
		const driverVendorId = selectedVehicle?.vendor_id || values?.vendor_id;
		if (!driverVendorId || !vehicleTypeId) return;

		await getDriverDataV2(
			driverVendorId,
			vehicleTypeId,
			values?.driver_id,
			values?.driver_name
		);
	}

	function handleDriverChange(driverId, setFieldValue) {
		setFieldValue('driver_id', driverId);
		const selectedDriver = (driverData || []).find(
			(driverOption) => Number(driverOption?.value) === Number(driverId)
		);
		setFieldValue('driver_number', selectedDriver?.item?.mobile_number || '');
		setFieldValue(
			'driver_name',
			selectedDriver?.item?.driver_name || selectedDriver?.item?.name || ''
		);
	}

	// eslint-disable-next-line sonarjs/cognitive-complexity
	function handlePincodeChange(pincode, type, setFieldValue) {
		if (pincode && pincode.length === 6) {
			dispatch(getPincodeList(pincode))
				.then((res) => {
					const data = res.rows[0];
					if (data) {
						const cityState = `${data.city || ''}${
							data.city && data.state ? ', ' : ''
						}${data.state || ''}`;
						if (type === 'source') {
							setSourceCity(cityState);
							setFieldValue('start_location_city_state', cityState);
							// Auto-populate city field if not already set
							if (data.city && formRef.current) {
								const currentCity = formRef.current.values?.city;
								if (!currentCity) {
									setFieldValue('city', data.city);
									// Trigger city change to load dependent data
									handleCitiesChange(data.city, setFieldValue);
								}
							}
						} else if (type === 'destination') {
							setDestCity(cityState);
							setFieldValue('end_location_city_state', cityState);
						}
					}
				})
				.catch(() => {
					message.error('Failed to fetch city and state for pincode');
				});
		} else if (type === 'source') {
			// Clear city/state when pincode is invalid or cleared
			setSourceCity('');
			setFieldValue('start_location_city_state', '');
		} else if (type === 'destination') {
			// Clear city/state when pincode is invalid or cleared
			setDestCity('');
			setFieldValue('end_location_city_state', '');
		}
	}

	async function handleProofUpload(file, setFieldValue) {
		// Check file size (5MB limit)
		if (file.size > 5 * 1024 * 1024) {
			message.error('File size must be less than 5 MB');
			return false;
		}

		setUploading(true);
		setUploadError('');
		try {
			const res = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: { entity: 'trip', keys: [file.name] },
				})
			);
			const fileData = res?.data?.[0];
			if (!fileData?.url || !fileData?.filename) throw new Error('Failed to get signed URL');
			const signedUrl = fileData.url;
			const s3Filename = fileData.filename;
			await fetch(signedUrl, {
				method: 'PUT',
				body: file,
				headers: { 'Content-Type': file.type },
			});
			setFieldValue('requirement_proof', [s3Filename]);
			message.success('File uploaded!');
		} catch (err) {
			setUploadError('Upload failed');
			message.error('Upload failed');
		} finally {
			setUploading(false);
		}
		return false;
	}

	// Helper function to build trip_info object
	const buildTripInfo = (values) => {
		const requirementProof =
			Array.isArray(values.requirement_proof) && values.requirement_proof.length > 0
				? values.requirement_proof
				: [''];
		return {
			client_rate: Number(values.client_rate),
			vendor_rate: Number(values.vendor_rate),
			is_inter_city: true,
			driver_name: values.driver_name || '',
			driver_number: values.driver_number || '',
			requirement_proof: requirementProof,
			remarks: values.remarks || '',
			// Cross-vendor vehicle remark (optional) — persisted in trips_v2.trip_info.
			...(values.cross_vendor_remark
				? { cross_vendor_vehicle_remark: values.cross_vendor_remark }
				: {}),
		};
	};

	// Helper function to build LR-specific fields
	const buildLRFields = (values) => ({
		// Sender fields
		sender_name: values.sender_name || '',
		sender_address: values.sender_address || '',
		sender_pincode: values.sender_pincode || values.start_location_pincode || '',
		// Receiver fields
		receiver_name: values.receiver_name || '',
		receiver_address: values.receiver_address || '',
		receiver_pincode: values.receiver_pincode || values.end_location_pincode || '',
		// LR-specific fields
		client_reference_numbers: Array.isArray(values.client_reference_numbers)
			? values.client_reference_numbers
			: [],
		ewb_number: values.ewb_number || '',
		goods_description: values.goods_description || '',
		declared_value: values.declared_value ? Number(values.declared_value) : undefined,
		gross_weight: values.gross_weight ? Number(values.gross_weight) : undefined,
		unit_count: values.unit_count ? Number(values.unit_count) : undefined,
		goods_details: Array.isArray(values.goods_details) ? values.goods_details : [],
	});

	// Helper function to build payload object
	const buildPayload = async (values, tripInfo) => {
		const vehicleTypeId = values.vehicle_type
			? await resolveVehicleTypeId(values.vehicle_type)
			: null;

		const reporting_date_payload = toUtcDateTime(values.reporting_date);
		const reporting_time_payload = toUtcTime(values.reporting_date);
		const start_date_payload = toUtcDateTime(values.start_date);
		const start_time_payload = toUtcTime(values.start_date);

		const shouldCreateLR = pendingValues?.action === 'yes';

		const basePayload = {
			trip_type: values.trip_type,
			city: values.city,
			project_id: values.project_id,
			route_id: values.route_id ? values.route_id : undefined,
			vendor_id: values.vendor_id,
			vehicle_type: values.vehicle_type,
			...(vehicleTypeId ? { vehicle_type_id: Number(vehicleTypeId) } : {}),
			vehicle_number: values.vehicle_number,
			driver_id: values.driver_id ? values.driver_id : undefined,
			driver_name: values.driver_name || '',
			driver_number: values.driver_number || '',
			reporting_date: reporting_date_payload,
			reporting_time: reporting_time_payload,
			start_date: start_date_payload,
			start_time: start_time_payload,
			destination_pincode: values.end_location_pincode,
			source_pincode: values.start_location_pincode,
			source_location: sourceCity || '',
			destination_location: destCity || '',
			info: tripInfo,
			...(isEdit ? { id } : {}),
			...(isEdit && values.start_km !== '' && values.start_km !== undefined
				? { start_km: Number(values.start_km) }
				: {}),
			...(shouldCreateLR ? { create_lr: true } : {}),
			adhoc_request_id: values.adhoc_request_id || '',
			remarks: values.remarks || '',
		};

		// Include LR fields when create_lr is true (matching old UI V1 structure)
		if (shouldCreateLR) {
			return {
				...basePayload,
				...buildLRFields(values),
			};
		}

		return basePayload;
	};

	// Helper function to handle successful trip creation
	const handleTripCreationSuccess = (res, setSubmitting, formValues) => {
		setSubmitting(false);
		setModalLoading(false);
		const newTripId = res?.data?.id || res?.id || res?.trip_id;

		if (isEdit) {
			message.success('Trip edited successfully');
			navigate('/trips/intercity');
			return;
		}

		if (pendingValues?.action === 'yes' && newTripId) {
			// Get current form values from formRef to ensure we have the latest values
			// This is important because driver_id and driver_number might be set after form submission
			const currentFormValues = formRef.current?.values || formValues || {};

			// Pass form values to CreateLR page via URL query params
			// Include all fields needed for LR creation (driver_id, driver_number, vehicle_type_id, sourceCity, destCity)
			const formValuesForLR = {
				// Spread formData first so any saved LR fields (sender, receiver, goods) are restored
				...formData,
				// Trip form values override formData trip fields
				...currentFormValues,
				source_location: sourceCity || '',
				destination_location: destCity || '',
				driver_id: currentFormValues.driver_id || formValues?.driver_id || undefined,
				driver_name: currentFormValues.driver_name || formValues?.driver_name || '',
				driver_number: currentFormValues.driver_number || formValues?.driver_number || '',
				vehicle_type_id:
					currentFormValues.vehicle_type_id || formValues?.vehicle_type_id || undefined,
			};

			const encoded = encodeURIComponent(JSON.stringify(formValuesForLR));
			navigate(`/trips/create-lr?data=${encoded}`, {
				state: { from: location.pathname },
			});
			return;
		}

		message.success('Trip created successfully without LR');
		navigate('/trips/intercity', { state: { refresh: true } });
	};
	const maybeReuploadPor = async (values) => {
		if (!isEdit) return;
		// after approved - no reupload
		const porApplies = ['pending', 'rejected'].includes(formData?.por_approval_status);
		const currentKey =
			Array.isArray(values.requirement_proof) && values.requirement_proof.length > 0
				? values.requirement_proof[0]
				: null;
		if (porApplies && currentKey && currentKey !== originalPorKeyRef.current) {
			await dispatch(reuploadPoR({ trip_id: Number(id) || id, document_key: currentKey }));
		}
	};

	const handleSubmit = async (values, { setSubmitting }) => {
		const tripInfo = buildTripInfo(values);
		const payload = await buildPayload(values, tripInfo);

		const requestData = {
			data: payload,
			...(isEdit ? { update_type: 'trip' } : { create_type: 'trip' }),
		};

		setModalLoading(true);
		dispatch(
			addUser({
				data: requestData,
				setErrors: () => {},
				isEditMode: isEdit,
				// Note: version parameter is ignored by addUser - it uses getUIVersion() instead
				// Since this component is only used in new UI, addUser will automatically use 'v2'
			})
		)
			.then(async (res) => {
				await maybeReuploadPor(values);
				handleTripCreationSuccess(res, setSubmitting, values);
			})
			.catch(() => {
				setSubmitting(false);
				setModalLoading(false);
			});
	};

	if (isEdit && (loading || !formData?.id)) {
		return <div>Loading...</div>;
	}

	return (
		<IntercityTripForm
			isEditMode={isEdit}
			formData={formData}
			cities={cities}
			trip_reason={trip_reason}
			projectData={projectData}
			routeData={routeData}
			vendorsData={vendorsData}
			vehicleTypes={VehicleTypes}
			vehicleTypeIdMap={vehicleTypeIdMap}
			vehicleNumbers={vehicleNumbers}
			driverData={driverData}
			isV2UI={isV2UI}
			projectAdhocFlag={ProjectAdhocFlag}
			sourceCity={sourceCity}
			destCity={destCity}
			uploading={uploading}
			uploadError={uploadError}
			vendorUserType={vendorUserType}
			showLRPrompt={showLRPrompt}
			setShowLRPrompt={setShowLRPrompt}
			pendingValues={pendingValues}
			setPendingValues={setPendingValues}
			modalLoading={modalLoading}
			formRef={formRef}
			onCityChange={handleCitiesChange}
			onProjectChange={handleProjectChange}
			onRouteChange={handleRouteChange}
			onVendorTypeChange={handleVendorTypeChange}
			onVendorChange={handleVendorChange}
			onVehicleTypeChange={handleVehicleTypeChange}
			onVehicleNumberChange={handleVehicleNumberChange}
			clientAllowsCrossVendor={crossVendorAllowed}
			onCrossVendorSelect={handleCrossVendorSelect}
			onDriverChange={handleDriverChange}
			onPincodeChange={handlePincodeChange}
			onProofUpload={handleProofUpload}
			onSubmit={handleSubmit}
			navigate={navigate}
			isvendorBilling={isvendorBilling}
		/>
	);
}
